// Analytics tracking utilities for GA4 events
// SINGLE SOURCE OF TRUTH for all GA4 event dispatching
// Provides wrapper functions for consistent event tracking across the application

import { ALLOWED_ANALYTICS_COUNTRIES, isHostnameAllowed } from '@/constants/geo';
import { getCurrentIntent, type IntentTier } from '@/lib/luxuryIntentScoring';

declare global {
  interface Window {
    gtag?: (
      command: string,
      targetId: string,
      config?: Record<string, any>
    ) => void;
  }
}

// Check if geo is blocked (set by GeoAwareAnalytics or pre-check script)
const isGeoBlocked = (): boolean => {
  if (typeof window === 'undefined') return true;
  if ((window as any).__GA_BLOCKED === true) return true;
  try {
    const cache = localStorage.getItem('geo_check');
    if (cache) {
      const data = JSON.parse(cache);
      if (data.blocked) return true;
      if (data.country_code && !ALLOWED_ANALYTICS_COUNTRIES.includes(data.country_code)) {
        return true;
      }
    }
  } catch (e) {}
  return false;
};

// Check if gtag is available, hostname is allowed, AND geo is not blocked
const isGtagAvailable = (): boolean => {
  return typeof window !== 'undefined' && 
         'gtag' in window && 
         typeof window.gtag === 'function' &&
         isHostnameAllowed() &&
         !isGeoBlocked();
};

// ============================================================
// CORE EVENT DISPATCHER
// All events include source_site for multi-brand segmentation
// ============================================================

export const trackEvent = (eventName: string, parameters?: Record<string, any>) => {
  if (isGtagAvailable()) {
    const enrichedParams = {
      ...parameters,
      source_site: 'yachtvalues',
    };
    window.gtag?.('event', eventName, enrichedParams);
    if (process.env.NODE_ENV === 'development') {
      console.log('📊 GA4 Event:', eventName, enrichedParams);
    }
  }
};

// ============================================================
// LUXURY EVENT TAXONOMY (17 standardized events)
// Each includes: yacht_length, price_band, builder_name,
// engine_type, geo_region, engagement_score, intent_score,
// session_quality_score, source_channel
// ============================================================

interface LuxuryEventParams {
  yacht_length?: number;
  price_band?: string;
  builder_name?: string;
  engine_type?: string;
  geo_region?: string;
  engagement_score?: number;
  intent_score?: number;
  intent_tier?: IntentTier;
  session_quality_score?: number;
  source_channel?: string;
  [key: string]: any;
}

function enrichWithIntentScore(params: LuxuryEventParams = {}): LuxuryEventParams {
  try {
    const { score, tier } = getCurrentIntent();
    return {
      ...params,
      intent_score: params.intent_score ?? score,
      intent_tier: params.intent_tier ?? tier,
    };
  } catch {
    return params;
  }
}

// Valuation events (luxury threshold)
export const trackLuxuryValuationSubmit = (params: LuxuryEventParams & { valuation_mid: number }) => {
  trackEvent('luxury_valuation_submit', enrichWithIntentScore(params));
};

export const trackSuperyachtValuationSubmit = (params: LuxuryEventParams & { valuation_mid: number }) => {
  trackEvent('superyacht_valuation_submit', enrichWithIntentScore(params));
};

// Builder & engine search
export const trackBuilderSearch = (builderName: string, params?: LuxuryEventParams) => {
  trackEvent('builder_search', enrichWithIntentScore({ ...params, builder_name: builderName }));
};

export const trackEngineTypeSearch = (engineType: string, params?: LuxuryEventParams) => {
  trackEvent('engine_type_search', enrichWithIntentScore({ ...params, engine_type: engineType }));
};

// Conversion events
export const trackCashOfferClickLuxury = (params?: LuxuryEventParams) => {
  trackEvent('cash_offer_click', enrichWithIntentScore(params));
};

export const trackBrokerageInquiry = (params?: LuxuryEventParams) => {
  trackEvent('brokerage_inquiry', enrichWithIntentScore(params));
};

export const trackOffMarketRequest = (params?: LuxuryEventParams) => {
  trackEvent('off_market_request', enrichWithIntentScore(params));
};

export const trackConciergeRequestLuxury = (params?: LuxuryEventParams) => {
  trackEvent('concierge_request', enrichWithIntentScore(params));
};

// Tool usage events
export const trackTradeUpUse = (params?: LuxuryEventParams) => {
  trackEvent('trade_up_use', enrichWithIntentScore(params));
};

export const trackNetProceedsUse = (params?: LuxuryEventParams) => {
  trackEvent('net_proceeds_use', enrichWithIntentScore(params));
};

export const trackDocumentUpload = (params?: LuxuryEventParams) => {
  trackEvent('document_upload', enrichWithIntentScore(params));
};

export const trackSpecDownload = (params?: LuxuryEventParams) => {
  trackEvent('spec_download', enrichWithIntentScore(params));
};

export const trackVirtualTourView = (params?: LuxuryEventParams) => {
  trackEvent('virtual_tour_view', enrichWithIntentScore(params));
};

// Engagement events
export const trackRepeatVisitor7d = (params?: LuxuryEventParams) => {
  trackEvent('repeat_visitor_7d', enrichWithIntentScore(params));
};

export const trackRepeatVisitor30d = (params?: LuxuryEventParams) => {
  trackEvent('repeat_visitor_30d', enrichWithIntentScore(params));
};

export const trackHighValueSession = (params?: LuxuryEventParams & { time_on_site_seconds?: number; scroll_depth?: number }) => {
  trackEvent('high_value_session', enrichWithIntentScore(params));
};

export const trackIntentEscalation = (params: LuxuryEventParams & { previous_tier: IntentTier; new_tier: IntentTier }) => {
  trackEvent('intent_escalation', enrichWithIntentScore(params));
};

// ============================================================
// EXISTING GA4 CONVENIENCE FUNCTIONS
// (Preserved for backward compatibility)
// ============================================================

export const trackButtonClick = (buttonName: string, location: string, destination?: string) => {
  trackEvent('button_click', { button_name: buttonName, location, destination });
};

export const trackCTAClick = (ctaName: string, destination: string, location: string) => {
  trackEvent('cta_click', { cta_name: ctaName, destination, location });
};

export const trackFormStart = (formName: string) => {
  trackEvent('form_start', { form_name: formName });
};

export const trackFormStep = (formName: string, step: number, stepName: string) => {
  trackEvent('form_step', { form_name: formName, step_number: step, step_name: stepName });
};

export const trackFormSubmit = (formName: string, success: boolean, errorMessage?: string) => {
  trackEvent('form_submit', { form_name: formName, success, error_message: errorMessage });
};

export const trackFormError = (formName: string, fieldName: string, errorType: string) => {
  trackEvent('form_error', { form_name: formName, field_name: fieldName, error_type: errorType });
};

export const trackGenerateLead = (leadType: string, source: string) => {
  trackEvent('generate_lead', { lead_type: leadType, source });
};

export const trackNavigation = (linkText: string, destination: string, location: string) => {
  trackEvent('navigation_click', { link_text: linkText, destination, location });
};

export const trackOutboundLink = (url: string, linkText: string, location: string) => {
  trackEvent('outbound_click', { url, link_text: linkText, location });
};

export const trackScrollDepth = (percentage: number, page: string) => {
  trackEvent('scroll', { percent_scrolled: percentage, page });
};

export const trackSearch = (searchTerm: string, resultsCount?: number) => {
  trackEvent('search', { search_term: searchTerm, results_count: resultsCount });
};

export const trackFileDownload = (fileName: string, fileType: string) => {
  trackEvent('file_download', { file_name: fileName, file_type: fileType });
};

export const trackVideoPlay = (videoTitle: string, videoUrl: string) => {
  trackEvent('video_play', { video_title: videoTitle, video_url: videoUrl });
};

export const trackEngagement = (action: string, category: string, label?: string) => {
  trackEvent('engagement', { action, category, label });
};

export const trackCaptchaComplete = (success: boolean) => {
  trackEvent('captcha_complete', { success });
};

export const trackPageView = (path: string, title?: string) => {
  if (isGtagAvailable()) {
    window.gtag?.('config', 'G-BD9WQCNXKH', { page_path: path, page_title: title });
  }
};

// Mobile-specific analytics
export const trackMobileInteraction = (action: string, details?: Record<string, any>) => {
  const isMobile = window.matchMedia('(max-width: 767px)').matches;
  const isTablet = window.matchMedia('(min-width: 768px) and (max-width: 1023px)').matches;
  trackEvent('mobile_interaction', {
    action,
    device_type: isMobile ? 'mobile' : isTablet ? 'tablet' : 'desktop',
    ...details,
  });
};

export const trackMobilePerformance = (metric: string, value: number) => {
  trackEvent('mobile_performance', {
    metric, value,
    connection_type: (navigator as any).connection?.effectiveType || 'unknown',
    device_memory: (navigator as any).deviceMemory || 'unknown',
  });
};

// Partner CTA tracking
export const trackPartnerCTAClick = (ctaType: string, pageType: string, placement: string) => {
  trackEvent('partner_cta_click', { cta_type: ctaType, page_type: pageType, placement });
};

export const trackPartnerCTAImpression = (ctaType: string, pageType: string, placement: string) => {
  trackEvent('partner_cta_impression', { cta_type: ctaType, page_type: pageType, placement });
};

// Buyer valuation tracking
export const trackBuyerValuationStart = (source: string, entryPoint: string, boatType?: string) => {
  trackEvent('buyer_valuation_start', { source, entry_point: entryPoint, boat_type: boatType });
};

export const trackBuyerValuationComplete = (boatType: string, lengthRange: string, newOrUsed: string) => {
  trackEvent('buyer_valuation_complete', { boat_type: boatType, length_range: lengthRange, new_or_used: newOrUsed });
};

export const trackSellerToBuyerConversion = (sellerValuationMid: number, boatType: string, context: string) => {
  trackEvent('seller_to_buyer_conversion', { seller_valuation_mid: sellerValuationMid, boat_type: boatType, context });
};

export const trackBuyerCTAClick = (location: string, variant: string, destination?: string) => {
  trackEvent('buyer_cta_click', { location, variant, destination: destination || '/buyer-valuation' });
};
